import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./genericBarChartCompare.module.scss";

interface CustomTooltipProps extends TooltipProps<any, any> {
  active?: boolean;
  payload?: any[];
  label?: any;
  allProducts: any[];
  isinToName: any;
}

// Custom tooltip component with proper typing
const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  allProducts,
  isinToName,
}) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "white",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "5px",
        }}
      >
        <p style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>{label}</p>
        {payload.map((entry, index) => {
          // Skip entries that aren't weight values
          if (!entry.dataKey.startsWith("weight")) return null;

          const productIndex = parseInt(entry.dataKey.replace("weight", ""));
          const productName = isinToName
            ? isinToName[allProducts[productIndex]]
            : allProducts[productIndex];

          return (
            <div
              key={index}
              style={{ display: "flex", alignItems: "center", margin: "5px 0" }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: entry.fill,
                  marginRight: "5px",
                }}
              />
              <span style={{ fontSize: 12 }}>
                {productName}: {entry.value.toFixed(2)}%
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

export const GenericChart = ({ data, title, allProducts, isinToName }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const barColors = [
    colorsConfig.paletteBarChart1,
    colorsConfig.paletteBarChart2,
    colorsConfig.paletteBarChart3,
    colorsConfig.paletteBarChart4,
    colorsConfig.paletteBarChart5,
    colorsConfig.paletteBarChart6,
  ];
  const labelColors = [
    "rgba(16, 57, 118, 0.2)",
    "rgba(0, 150, 136, 0.20)",
    "rgba(92, 168, 230, 0.20)",
    "rgba(52, 161, 178, 0.20)",
    "rgba(44, 112, 212, 0.20)",
  ];

  const createLabelRenderer = (barIndex) => {
    return (props) => {
      const { x, y, width, height, value } = props;
      if (value === undefined || value === null || isNaN(value)) return null;

      const textContent = `${value.toFixed(2)}%`;
      const textWidth = textContent.length * 8;
      const padding = 0;
      const rectWidth = textWidth + padding * 2;

      // Use the passed barIndex to determine background color
      const backgroundColor = labelColors[barIndex % labelColors.length];

      return (
        <g>
          <rect
            x={x + width + 7}
            y={y + height / 2 - 12}
            width={rectWidth}
            height={23}
            fill={backgroundColor}
            rx={5}
          />
          <text
            x={x + width + 12}
            y={y + height / 2 + 0}
            fill={colorsConfig.FinecoBlack}
            dominantBaseline="middle"
            textAnchor="start"
            fontSize={12}
            fontWeight={700}
            fontFamily="Proxima Nova"
          >
            {`${value === 0 ? "0.00" : value.toFixed(2)}%`}
          </text>
        </g>
      );
    };
  };

  // Ensure one Y-axis label per asset group
  const flattenedData = data.map((group) => ({
    label: group.label,
    ...group.weights.reduce((acc, weight, index) => {
      acc[`weight${index}`] = weight;
      return acc;
    }, {}),
  }));

  // Find max value for better scaling
  const maxWeight = Math.max(...data.flatMap((group) => group.weights));
  // Generate ticks at steps of 50
  const ticks = [];
  for (let i = 0; i <= Math.ceil(maxWeight / 50) * 50; i += 50) {
    ticks.push(i);
  }

  const renderYAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={-10}
          y={0}
          dy={4}
          textAnchor="end"
          fill={colorsConfig.FinecoBlue} // Custom color
          fontFamily="Proxima Nova, Arial, sans-serif" // Custom font
          fontSize={14} // Custom font size
          fontWeight={400} // Font weight
        >
          {payload.value}
        </text>
      </g>
    );
  };

  // Custom render function for XAxis ticks
  const renderXAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill={colorsConfig.FinecoGray} // Custom color - adjust as needed
          fontFamily="Proxima Nova, Arial, sans-serif"
          fontSize={12}
          fontWeight={500}
          fontStyle="normal"
        >
          {`${payload.value}%`}
        </text>
      </g>
    );
  };

  return (
    <div>
      <div className={styles.chartContainer}>
        <ResponsiveContainer
          width="100%"
          height={flattenedData.length * 150 + 40}
          debounce={500}
        >
          <BarChart
            data={flattenedData}
            layout="vertical"
            margin={{ top: -8, right: 80, left: 100, bottom: 15 }}
            barGap={3} // Adjusted spacing
            barCategoryGap="-10%"
          >
            <CartesianGrid horizontal={false} vertical={false} />
            <XAxis
              type="number"
              domain={[0, Math.ceil(maxWeight / 50) * 50]}
              ticks={ticks}
              tickLine={false}
              axisLine={{ stroke: colorsConfig.Gray3 }}
              tick={renderXAxisTick}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={80}
              axisLine={{ stroke: colorsConfig.Gray3 }}
              tickLine={false}
              tick={renderYAxisTick}
            />
            <Tooltip
              content={(props) => (
                <CustomTooltip
                  {...props}
                  allProducts={allProducts}
                  isinToName={isinToName}
                />
              )}
            />
            {allProducts.map((_, index) => (
              <Bar
                key={`bar-${index}`}
                dataKey={`weight${index}`}
                radius={[0, 2, 2, 0]}
                barSize={24}
                minPointSize={1}
                fill={barColors[index % barColors.length]}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey={`weight${index}`}
                  position="right"
                  content={createLabelRenderer(index)}
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>

        {/* Legend at the bottom */}
        <div className={styles.legendContainer}>
          {allProducts.map((product, index) => (
            <div key={`legend-${index}`} className={styles.legendItem}>
              <div
                className={styles.legendColor}
                style={{ backgroundColor: barColors[index % barColors.length] }}
              />
              <span className={styles.legendText}>
                {isinToName ? isinToName[product] : product}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const GenericBarChartCompare = ({ data, title, allProducts, isinToName }) => {
  return (
    <>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={styles.container}>
        <GenericChart
          data={data}
          title={title}
          allProducts={allProducts}
          isinToName={isinToName}
        />
      </div>
    </>
  );
};

export default GenericBarChartCompare;
